import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, Search } from 'lucide-react'
import type { SelectOption } from './Select'
import { usePopover } from './usePopover'

const normalize = (text: string) => text.trim().replace(/\s+/g, ' ').toLowerCase()

/** Options whose label contains the query, prefix matches first. Order is otherwise kept. */
export function searchOptions(options: SelectOption[], query: string): SelectOption[] {
  const needle = normalize(query)
  if (!needle) return options
  const prefix: SelectOption[] = []
  const inner: SelectOption[] = []
  for (const option of options) {
    const label = normalize(option.label)
    if (label.startsWith(needle)) prefix.push(option)
    else if (label.includes(needle)) inner.push(option)
  }
  return [...prefix, ...inner]
}

function Highlight({ label, query }: { label: string; query: string }): ReactNode {
  const needle = normalize(query)
  const at = needle ? label.replace(/\s+/g, ' ').toLowerCase().indexOf(needle) : -1
  if (at < 0) return label
  return (
    <>
      {label.slice(0, at)}
      <mark className="wt-combobox-match">{label.slice(at, at + needle.length)}</mark>
      {label.slice(at + needle.length)}
    </>
  )
}

/**
 * A select whose list opens with a search field. Typing filters the real
 * options, and the strongest prefix match is shown as a ghost completion that
 * only commits on Enter, Tab or a click. Label it with `<label htmlFor={id}>`.
 */
export function SearchCombobox({
  id,
  value,
  options,
  onChange,
  searchLabel,
  placeholder,
  emptyText,
  disabled = false,
}: {
  id: string
  value: string
  options: SelectOption[]
  onChange: (value: string) => void
  searchLabel: string
  placeholder: string
  emptyText: string
  disabled?: boolean
}) {
  const popover = usePopover<HTMLButtonElement>({ minWidth: 260 })
  const listId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const selected = options.find((option) => option.value === value)
  const results = useMemo(() => searchOptions(options, query), [options, query])
  const optionId = (index: number) => `${listId}-${index}`

  // The ghost completion: only when the typed text is a prefix of the top result.
  const top = results[0]
  const typed = query.replace(/^\s+/, '')
  const ghost =
    typed && top && top.label.toLowerCase().startsWith(typed.toLowerCase()) && top.label.length > typed.length
      ? top.label.slice(typed.length)
      : ''

  useEffect(() => {
    if (popover.isOpen) inputRef.current?.focus()
  }, [popover.isOpen])

  useEffect(() => {
    if (popover.isOpen) document.getElementById(optionId(active))?.scrollIntoView?.({ block: 'nearest' })
  })

  function openList(initial = '') {
    setQuery(initial)
    const selectedIndex = initial ? 0 : options.findIndex((option) => option.value === value)
    setActive(Math.max(selectedIndex, 0))
    popover.open()
  }

  function choose(option: SelectOption | undefined) {
    popover.close()
    if (option && option.value !== value) onChange(option.value)
  }

  function onTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      openList()
    } else if (event.key.length === 1 && event.key !== ' ' && !event.metaKey && !event.ctrlKey && !event.altKey) {
      // Start searching straight from the closed control.
      event.preventDefault()
      openList(event.key)
    }
  }

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    const last = results.length - 1
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault()
        const step = event.key === 'ArrowDown' ? 1 : -1
        setActive((index) => Math.min(Math.max(index + step, 0), Math.max(last, 0)))
        return
      }
      case 'Home':
      case 'End':
        if (query) return
        event.preventDefault()
        setActive(event.key === 'Home' ? 0 : Math.max(last, 0))
        return
      case 'Enter':
        event.preventDefault()
        choose(results[active])
        return
      case 'Escape':
        // Keep a surrounding sheet or modal from closing along with the list.
        event.preventDefault()
        event.stopPropagation()
        popover.close()
        return
      case 'Tab':
        // Tab accepts a visible completion and hands focus back to the control,
        // so the next Tab continues through the form. Without one it just closes.
        if (ghost) {
          event.preventDefault()
          choose(top)
        } else {
          popover.close()
        }
        return
    }
  }

  return (
    <>
      <button
        aria-controls={popover.isOpen ? listId : undefined}
        aria-expanded={popover.isOpen}
        aria-haspopup="listbox"
        className="wt-select-trigger"
        data-open={popover.isOpen || undefined}
        disabled={disabled}
        id={id}
        onClick={() => (popover.isOpen ? popover.close() : openList())}
        onKeyDown={onTriggerKeyDown}
        ref={popover.triggerRef}
        type="button"
      >
        <span className="wt-select-value">
          <span>{selected?.label ?? ''}</span>
        </span>
        <ChevronDown aria-hidden="true" className="wt-select-chevron" size={15} />
      </button>

      {popover.phase !== 'closed'
        ? createPortal(
            <div
              className="wt-popover wt-select-panel wt-combobox-panel"
              data-state={popover.phase}
              ref={popover.panelRef}
              style={popover.style}
            >
              <div className="wt-combobox-search">
                <Search aria-hidden="true" size={15} />
                <div className="wt-combobox-field">
                  <span aria-hidden="true" className="wt-combobox-ghost">
                    <span className="wt-combobox-ghost-typed">{query}</span>
                    {ghost}
                  </span>
                  <input
                    aria-activedescendant={results.length ? optionId(active) : undefined}
                    aria-autocomplete="both"
                    aria-controls={listId}
                    aria-expanded="true"
                    aria-label={searchLabel}
                    autoComplete="off"
                    onChange={(event) => {
                      setQuery(event.target.value)
                      setActive(0)
                    }}
                    onKeyDown={onInputKeyDown}
                    placeholder={placeholder}
                    ref={inputRef}
                    role="combobox"
                    spellCheck={false}
                    type="text"
                    value={query}
                  />
                </div>
              </div>
              {results.length ? (
                <ul id={listId} role="listbox">
                  {results.map((option, index) => (
                    <li
                      aria-selected={option.value === value}
                      className="wt-select-option"
                      data-active={index === active || undefined}
                      id={optionId(index)}
                      key={option.value}
                      onClick={() => choose(option)}
                      // Keeps focus in the search field.
                      onMouseDown={(event) => event.preventDefault()}
                      onMouseEnter={() => setActive(index)}
                      role="option"
                    >
                      {option.icon}
                      <span>
                        <Highlight label={option.label} query={query} />
                      </span>
                      {option.value === value ? <Check aria-hidden="true" className="wt-select-check" size={14} /> : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="wt-combobox-empty" id={listId} role="status">
                  {emptyText}
                </p>
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
